<template>
  <div class="grid grid-cols-[1fr_auto_auto] items-center gap-4 px-6 py-4 border-t border-gray-50 dark:border-gray-800/60 hover:bg-gray-50/50 dark:hover:bg-gray-800/20 transition-colors">

    <!-- Member info -->
    <div class="flex items-center gap-3 min-w-0">
      <div class="flex-shrink-0">
        <img v-if="member.profile_picture_url" :src="member.profile_picture_url" :alt="member.display_name"
          class="w-10 h-10 rounded-full object-cover"/>
        <div v-else :class="avatarColorClass" class="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold">
          {{ member.avatar_name }}
        </div>
      </div>
      <div class="min-w-0">
        <div class="flex items-center gap-1.5">
          <p class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{{ member.display_name }}</p>
          <span v-if="member.designation" class="text-xs text-gray-400 dark:text-gray-500 truncate">({{ member.designation }})</span>
        </div>
        <div class="flex items-center gap-1.5 mt-0.5">
          <p class="text-xs text-gray-400 dark:text-gray-500 truncate">{{ member.user_email }}</p>
          <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0" :class="statusClass">
            <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="statusDotClass"></span>
            {{ statusLabel }}
          </span>
        </div>
      </div>
    </div>

    <!-- Role -->
    <div class="w-24 flex justify-center" ref="roleTriggerRef">
      <span v-if="member.org_owner" class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400">
        <svg class="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2.5 8.5 5 15h10l2.5-6.5-4 2L10 6 6.5 10.5l-4-2Z"/>
        </svg>
        Owner
      </span>
      <button v-else-if="member.show_role_control" type="button" @click="toggleRoleMenu"
        class="w-full flex items-center justify-between gap-1 px-2.5 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
        {{ formatRole(member.role) }}
        <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
        </svg>
      </button>
      <p v-else class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ formatRole(member.role) }}</p>
    </div>

    <!-- Actions -->
    <div class="w-8 flex justify-end" ref="actionsTriggerRef">
      <button v-if="member.show_actions_dropdown" type="button" @click="toggleActionsMenu"
        class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 16 3">
          <path d="M2 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm6.041 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM14 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z"/>
        </svg>
      </button>
    </div>

    <!-- Role menu -->
    <Teleport to="body">
      <div v-if="roleMenuOpen" class="fixed inset-0 z-[9998]" @click="roleMenuOpen = false"></div>
      <div v-if="roleMenuOpen" class="fixed z-[9999] w-64 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-[#2A3037] p-2" :style="roleMenuStyle">
        <button type="button" @click="selectRole('admin')"
          class="w-full rounded-lg text-left py-2 px-3 transition-colors" :class="member.role === 'admin' ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700'">
          <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.features.organization.admin }}</p>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ I18N.features.organization.admin_subheading }}</p>
        </button>
        <button type="button" @click="selectRole('member')"
          class="w-full rounded-lg text-left py-2 px-3 transition-colors" :class="member.role === 'member' ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700'">
          <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.features.organization.member }}</p>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ I18N.features.organization.member_subheading }}</p>
        </button>
      </div>
    </Teleport>

    <!-- Actions menu -->
    <Teleport to="body">
      <div v-if="actionsMenuOpen" class="fixed inset-0 z-[9998]" @click="actionsMenuOpen = false"></div>
      <div v-if="actionsMenuOpen" class="fixed z-[9999] w-64 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-[#2A3037] p-2" :style="actionsMenuStyle">
        <button v-if="member.show_edit_btn" type="button" @click="onEdit"
          class="w-full rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-left py-2 px-3 transition-colors">
          <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.features.organization.edit }}</p>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ I18N.features.organization.edit_subheading }}</p>
        </button>
        <button v-if="member.show_re_invite" type="button" @click="onResend"
          class="w-full rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-left py-2 px-3 transition-colors">
          <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.features.organization.resend_invitation }}</p>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ I18N.features.organization.resend_invitation_subheading }}</p>
        </button>
        <button v-if="member.show_remove_btn" type="button" @click="onRemove"
          class="w-full rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-left py-2 px-3 transition-colors">
          <p class="text-sm font-medium text-red-600 dark:text-red-400">{{ I18N.features.organization.remove_member }}</p>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ I18N.features.organization.remove_member_subheading }}</p>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useConstants } from 'composables/useConstants.js'

const { I18N } = useConstants()

const props = defineProps({
  member: { type: Object, required: true }
})

// Same palette used for organization avatars (see app/assets/tailwind/application.css)
const AVATAR_COLORS = ['peacock', 'sage', 'tangerine', 'grape', 'flamingo', 'blueberry', 'basil', 'tomato', 'lavender', 'graphite', 'banana']

function hashToIndex(str, length) {
  let hash = 0
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) % length
  return hash
}

const avatarColorClass = computed(() => {
  const key = (props.member.email || props.member.display_name || '').toLowerCase()
  return AVATAR_COLORS[hashToIndex(key, AVATAR_COLORS.length)]
})

const emit = defineEmits(['role-change', 'edit', 'resend', 'remove'])

const roleTriggerRef = ref(null)
const actionsTriggerRef = ref(null)
const roleMenuOpen = ref(false)
const actionsMenuOpen = ref(false)
const roleMenuStyle = ref({})
const actionsMenuStyle = ref({})

const statusLabel = computed(() => {
  if (props.member.status === 'accepted') return I18N.features.organization.accepted
  if (props.member.status === 'declined') return I18N.features.organization.declined
  return I18N.features.organization.pending
})

const statusClass = computed(() => {
  if (props.member.status === 'accepted') return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
  if (props.member.status === 'declined') return 'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400'
  return 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
})

const statusDotClass = computed(() => {
  if (props.member.status === 'accepted') return 'bg-emerald-500'
  if (props.member.status === 'declined') return 'bg-red-400'
  return 'bg-blue-400'
})

function formatRole(role) {
  if (!role) return ''
  return role.charAt(0).toUpperCase() + role.slice(1)
}

function positionMenu(triggerEl) {
  const rect = triggerEl.getBoundingClientRect()
  return {
    top: `${rect.bottom + 6}px`,
    right: `${window.innerWidth - rect.right}px`,
  }
}

function toggleRoleMenu() {
  actionsMenuOpen.value = false
  if (!roleMenuOpen.value) roleMenuStyle.value = positionMenu(roleTriggerRef.value)
  roleMenuOpen.value = !roleMenuOpen.value
}

function toggleActionsMenu() {
  roleMenuOpen.value = false
  if (!actionsMenuOpen.value) actionsMenuStyle.value = positionMenu(actionsTriggerRef.value)
  actionsMenuOpen.value = !actionsMenuOpen.value
}

function selectRole(role) {
  roleMenuOpen.value = false
  if (role !== props.member.role) emit('role-change', role)
}

function onEdit() {
  actionsMenuOpen.value = false
  emit('edit')
}

function onResend() {
  actionsMenuOpen.value = false
  emit('resend')
}

function onRemove() {
  actionsMenuOpen.value = false
  emit('remove')
}
</script>
